// 画布气泡显示样式（Sprint 6.1 起 scale 同步作用于导出 PDF；
// Sprint 6.2 起**全部字段**均同步作用于导出 PDF —— 引线/描点开关、颜色、
// 粗细、字号、大小，导出与画布所见即所得）。
//
// ★ v0.77（用户 2026-10-19）：「样式那里的设置能不能只对当前图纸有效，不要全部应用」。
//   原来是**一份全局偏好**（单键 `gdt_balloon_style_v1`），在图纸 A 上把气泡改成
//   蓝色加粗，切到图纸 B 也跟着变 —— 因为两处读的是同一格 localStorage。
//   现在改为**按图纸分键**：`gdt_balloon_style_v2:<drawingId>`。
//
// ★ 旧键仍然保留，但只作为「本图纸还没单独设过」的**继承基线**（只读不写）：
//     · 图纸无自己的记录 → 用旧键的值（用户此前调好的那套不会凭空消失）；
//     · 用户在本图纸上改动 → 只写本图纸的键，永远不再写旧键；
//     · 「重置」→ 往本图纸的键写一份出厂默认（**不是删键**：删键会回落到旧键，
//       与「重置」的预期相反）。
//   ⇒ 结果：改 A 不影响 B；B 保持旧键或出厂默认；切回 A 仍是 A 自己的设置。

export interface BalloonStyle {
  /** 气泡半径放大倍数（相对后端默认半径） */
  scale: number;
  /** 气泡主色（描边 + 编号） */
  color: string;
  /** 气泡描边线宽（屏像素，选中/缩放前基线） */
  lineWidth: number;
  /** 是否绘制引线 */
  showLeader: boolean;
  /** 是否绘制描点（引线指向特征锚点处的端点标记） */
  showAnchor: boolean;
  /** 编号字号放大倍数（相对默认） */
  fontScale: number;
}

/** v0.77：按图纸分键的前缀。键 = `gdt_balloon_style_v2:<drawingId>` */
const KEY_PREFIX = 'gdt_balloon_style_v2:';
/** 旧版本那份**全局**偏好。现在只读、只作继承基线（见文件头注释）。 */
const LEGACY_KEY = 'gdt_balloon_style_v1';

// ★ 取值范围「单一出处」：滑块（BalloonStylePanel）与载入钳位共用同一常量。
//   踩过的坑（2026-09-29）：滑块 min={0.6} 而这里的 clamp 也写 0.6，
//   两处各写一遍 → 想放开下限必须同时改两处，漏一处就是「静默失效」。
export const SCALE_RANGE = { min: 0.2, max: 1.8, step: 0.05 } as const;
export const FONT_SCALE_RANGE = { min: 0.3, max: 2, step: 0.05 } as const;

export const DEFAULT_STYLE: BalloonStyle = {
  scale: 1,
  color: '#e53935',
  lineWidth: 1.6,
  showLeader: true,
  showAnchor: true,
  fontScale: 1,
};

/** 图纸 id → 存储键。没有有效 id（null/0/NaN）时返回 null = 用旧键。 */
function keyOf(drawingId?: number | null): string | null {
  const n = Number(drawingId);
  return Number.isFinite(n) && n > 0 ? `${KEY_PREFIX}${n}` : null;
}

function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

/** 宽松解析：任何字段缺省/类型不对/超范围都退回默认值，绝不抛。 */
function parse(raw: string | null): BalloonStyle {
  if (!raw) return { ...DEFAULT_STYLE };
  try {
    const p = JSON.parse(raw);
    return {
      scale: clampNum(p.scale, SCALE_RANGE.min, SCALE_RANGE.max, DEFAULT_STYLE.scale),
      color: typeof p.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(p.color) ? p.color : DEFAULT_STYLE.color,
      lineWidth: clampNum(p.lineWidth, 0.5, 4, DEFAULT_STYLE.lineWidth),
      showLeader: typeof p.showLeader === 'boolean' ? p.showLeader : DEFAULT_STYLE.showLeader,
      showAnchor: typeof p.showAnchor === 'boolean' ? p.showAnchor : DEFAULT_STYLE.showAnchor,
      fontScale: clampNum(p.fontScale, FONT_SCALE_RANGE.min, FONT_SCALE_RANGE.max, DEFAULT_STYLE.fontScale),
    };
  } catch {
    return { ...DEFAULT_STYLE };
  }
}

/** 这张图纸是否**单独存过**样式（false = 正沿用旧键/出厂默认）。 */
export function hasOwnStyle(drawingId?: number | null): boolean {
  const k = keyOf(drawingId);
  return !!k && read(k) != null;
}

/**
 * 读取样式。
 * @param drawingId 当前图纸 id —— **界面上一律要传**，否则读到的是旧键那份全局值。
 */
export function loadBalloonStyle(drawingId?: number | null): BalloonStyle {
  const k = keyOf(drawingId);
  if (k) {
    const raw = read(k);
    if (raw != null) return parse(raw);      // 本图纸自己的
  }
  return parse(read(LEGACY_KEY));            // 没设过 ⇒ 继承旧偏好（只读，不写盘）
}

/**
 * 保存样式。传了图纸 id 就**只**写这张图纸的键，不再动全局键。
 * @param drawingId 当前图纸 id；不传 = 写旧键（只应出现在脚本/无图纸上下文的场景）
 */
export function saveBalloonStyle(s: BalloonStyle, drawingId?: number | null) {
  const k = keyOf(drawingId) || LEGACY_KEY;
  try { localStorage.setItem(k, JSON.stringify(s)); } catch { /* ignore */ }
}

/**
 * 恢复出厂默认。
 * 有图纸 id ⇒ 把默认值写进本图纸的键（不是删键，见文件头）；
 * 无 id ⇒ 删掉旧键。
 */
export function resetBalloonStyle(drawingId?: number | null): BalloonStyle {
  const k = keyOf(drawingId);
  try {
    if (k) localStorage.setItem(k, JSON.stringify(DEFAULT_STYLE));
    else localStorage.removeItem(LEGACY_KEY);
  } catch { /* ignore */ }
  return { ...DEFAULT_STYLE };
}

function clampNum(v: unknown, lo: number, hi: number, dflt: number): number {
  const n = typeof v === 'number' ? v : dflt;
  if (Number.isNaN(n)) return dflt;
  return Math.min(hi, Math.max(lo, n));
}

/**
 * 样式 → 后端查询参数的**唯一映射**（v0.77）。
 *
 * ⚠️ 以前 `exportBalloonPdf` / `exportSip` / `exportBatch` 各自手抄一遍
 *    `scale/leader/anchor/color/line_width/font_scale` 六个字段。三份实现
 *    是「加了字段有一处没跟」的温床 —— 收口到这里，新增字段只改这一处。
 */
export function styleQueryParams(s: BalloonStyle): Record<string, unknown> {
  return {
    scale: s.scale,
    leader: s.showLeader,
    anchor: s.showAnchor,
    color: s.color || '',
    line_width: s.lineWidth,
    font_scale: s.fontScale,
  };
}

// ⚠️ 检测工具候选的**唯一出处**已收敛到 `lib/annot.ts` 的 `METHOD_OPTS`（v0.31.5）。
// 这里只做 re-export，防止老引用断掉；本文件不再自己维护一份字符串数组 ——
// 两份实现分叉后改一份另一份不跟，就是「改了没反应」的经典成因。
export { METHOD_OPTS as METHOD_OPTS_SHARED } from './annot';
